import api, { getErrorMessage } from './api';
import { mockCourseService } from './mockData';

// Use mock data for demo (set to false when backend is available)
const USE_MOCK_DATA = true;

// Course types
export interface Course {
  id: string;
  title: string;
  description: string;
  short_description: string;
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  status: 'draft' | 'published' | 'archived';
  estimated_duration_hours: number;
  cover_image_url: string | null;
  tags: string[];
  prerequisites: string[];
  learning_objectives: string[];
  enrollment_count: number;
  average_rating: number;
  is_featured: boolean;
  is_free: boolean;
  created_at: string;
  updated_at: string;
}

export interface CourseWithLessons extends Course {
  lessons: Lesson[];
}

export interface Lesson {
  id: string;
  course_id: string;
  title: string;
  description: string;
  content: string;
  lesson_type: 'text' | 'video' | 'interactive' | 'quiz' | 'coding' | 'assignment';
  order_index: number;
  estimated_duration_minutes: number;
  video_url: string | null;
  resources: Record<string, any>;
  is_preview: boolean;
  is_required: boolean;
  points_value: number;
  created_at: string;
  updated_at: string;
}

export interface LessonWithProgress extends Lesson {
  is_completed: boolean;
  is_started: boolean;
  completion_percentage: number;
  points_earned: number;
  time_spent_minutes: number;
}

export interface UserProgress {
  id: string;
  user_id: string;
  course_id: string;
  is_enrolled: boolean;
  is_completed: boolean;
  completion_percentage: number;
  total_lessons: number;
  completed_lessons: number;
  total_points_earned: number;
  total_time_spent_minutes: number;
  enrolled_at: string;
  last_accessed_at: string | null;
  completed_at: string | null;
  current_lesson_id: string | null;
}

export interface CourseListResponse {
  courses: Course[];
  total: number;
  page: number;
  page_size: number;
}

export interface CourseFilters {
  difficulty_level?: string;
  tags?: string[];
  is_featured?: boolean;
  is_free?: boolean;
  page?: number;
  page_size?: number;
}

// Course service functions
export const courseService = {
  /**
   * Get list of published courses with optional filters
   */
  async getCourses(filters: CourseFilters = {}): Promise<CourseListResponse> {
    if (USE_MOCK_DATA) {
      return mockCourseService.getCourses(filters);
    }
    
    try {
      const params = new URLSearchParams();
      if (filters.difficulty_level) params.append('difficulty_level', filters.difficulty_level);
      if (filters.is_featured !== undefined) params.append('is_featured', String(filters.is_featured));
      if (filters.is_free !== undefined) params.append('is_free', String(filters.is_free));
      if (filters.page) params.append('page', String(filters.page));
      if (filters.page_size) params.append('page_size', String(filters.page_size));
      if (filters.tags) {
        filters.tags.forEach(tag => params.append('tags', tag));
      }

      const response = await api.get<CourseListResponse>(`/api/v1/courses?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get featured courses
   */
  async getFeaturedCourses(): Promise<Course[]> {
    if (USE_MOCK_DATA) {
      return mockCourseService.getFeaturedCourses();
    }
    
    try {
      const response = await api.get<Course[]>('/api/v1/courses/featured');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Search courses by query
   */
  async searchCourses(query: string, page: number = 1, pageSize: number = 10): Promise<CourseListResponse> {
    if (USE_MOCK_DATA) {
      return mockCourseService.searchCourses(query, page, pageSize);
    }
    
    try {
      const response = await api.get<CourseListResponse>(
        `/api/v1/courses/search?q=${encodeURIComponent(query)}&page=${page}&page_size=${pageSize}`
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get a single course by ID with lessons
   */
  async getCourse(courseId: string): Promise<CourseWithLessons> {
    try {
      const response = await api.get<CourseWithLessons>(`/api/v1/courses/${courseId}?include_lessons=true`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get lessons for a course
   */
  async getCourseLessons(courseId: string): Promise<Lesson[]> {
    try {
      const response = await api.get<Lesson[]>(`/api/v1/courses/${courseId}/lessons`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get a single lesson by ID
   */
  async getLesson(lessonId: string): Promise<Lesson> {
    try {
      const response = await api.get<Lesson>(`/api/v1/lessons/${lessonId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Enroll in a course
   */
  async enrollInCourse(courseId: string): Promise<UserProgress> {
    if (USE_MOCK_DATA) {
      return mockCourseService.enrollInCourse(courseId);
    }
    
    try {
      const response = await api.post<UserProgress>('/api/v1/progress/enroll', { course_id: courseId });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get user's progress for a course
   */
  async getCourseProgress(courseId: string): Promise<UserProgress> {
    try {
      const response = await api.get<UserProgress>(`/api/v1/progress/courses/${courseId}/progress`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get all user enrollments
   */
  async getUserEnrollments(): Promise<UserProgress[]> {
    if (USE_MOCK_DATA) {
      return mockCourseService.getUserEnrollments();
    }
    
    try {
      const response = await api.get<UserProgress[]>('/api/v1/progress/enrollments');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Start a lesson
   */
  async startLesson(lessonId: string): Promise<void> {
    try {
      await api.post(`/api/v1/progress/lessons/${lessonId}/start`);
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update lesson progress
   */
  async updateLessonProgress(
    lessonId: string,
    data: {
      completion_percentage?: number;
      time_spent_minutes?: number;
      is_completed?: boolean;
      score?: number;
      max_score?: number;
    }
  ): Promise<void> {
    try {
      await api.put('/api/v1/progress/lessons/progress', {
        lesson_id: lessonId,
        ...data,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get lessons with progress for a course
   */
  async getCourseLessonsWithProgress(courseId: string): Promise<LessonWithProgress[]> {
    try {
      const response = await api.get<LessonWithProgress[]>(
        `/api/v1/progress/courses/${courseId}/lessons-with-progress`
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};
